import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { 
  ArrowLeft, Calendar, Target, CheckCircle, Clock, 
  Star, Trophy, Zap, TrendingUp, BookOpen, Users,
  AlertCircle, Play, ChevronRight, Award
} from 'lucide-react';
import axios from 'axios';

const ActionPlan = () => {
  const navigate = useNavigate();
  const [actionPlan, setActionPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchActionPlan();
  }, []);

  const fetchActionPlan = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      console.log('🎯 Fetching personalized action plan...');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/coaching/action-plan`,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000
        }
      );
      
      console.log('✅ Action plan loaded:', response.data);
      setActionPlan(response.data);
    } catch (error) {
      console.error('❌ Error fetching action plan:', error);
      if (error.response?.status === 402) {
        navigate('/subscription/plans');
      } else {
        setError('Unable to load your action plan. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBackToDashboard = () => {
    navigate('/coaching/dashboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-400 mx-auto mb-4"></div>
              <p className="text-white">Loading your personalized action plan...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
        <div className="max-w-4xl mx-auto">
          <Button
            onClick={handleBackToDashboard}
            variant="outline"
            className="mb-6 border-white/20 text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <Card className="bg-white/5 backdrop-blur-md border-white/10">
            <CardContent className="p-8 text-center">
              <AlertCircle className="w-16 h-16 text-rose-400 mx-auto mb-4" />
              <h2 className="text-xl text-white mb-2">Unable to Load Action Plan</h2>
              <p className="text-gray-300 mb-4">{error}</p>
              <Button onClick={fetchActionPlan} className="bg-rose-600 hover:bg-rose-700">
                Try Again
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            onClick={handleBackToDashboard}
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <div className="flex items-center space-x-2">
            <Target className="w-6 h-6 text-rose-400" />
            <h1 className="text-2xl font-bold text-white">Your Action Plan</h1>
          </div>
        </div>

        {actionPlan?.action_plan ? (
          <div className="space-y-6">
            {/* Plan Overview */}
            <Card className="bg-white/5 backdrop-blur-md border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <Trophy className="w-5 h-5 mr-2 text-rose-400" />
                  {actionPlan.action_plan.plan_title || 'Your Personalized Dating Success Plan'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <div className="flex items-center mb-2">
                      <Calendar className="w-4 h-4 text-rose-400 mr-2" />
                      <span className="text-gray-300">Duration:</span>
                    </div>
                    <p className="text-white font-semibold mb-4">
                      {actionPlan.action_plan.duration || '30 days'}
                    </p>
                  </div>
                  
                  {actionPlan.action_plan.focus_areas && (
                    <div>
                      <div className="flex items-center mb-2">
                        <Target className="w-4 h-4 text-rose-400 mr-2" />
                        <span className="text-gray-300">Focus Areas:</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {actionPlan.action_plan.focus_areas.map((area, index) => (
                          <Badge key={index} className="bg-rose-500/20 text-rose-400 border-rose-500/30">
                            {area}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Weekly Goals */}
            {actionPlan.action_plan.weekly_goals && actionPlan.action_plan.weekly_goals.length > 0 && (
              <div className="space-y-4">
                <h2 className="text-xl font-bold text-white flex items-center">
                  <Calendar className="w-5 h-5 mr-2 text-rose-400" />
                  Weekly Roadmap
                </h2>
                
                {actionPlan.action_plan.weekly_goals.map((week, index) => (
                  <Card key={index} className="bg-white/5 backdrop-blur-md border-white/10">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center">
                        <span className="bg-rose-500/20 text-rose-400 px-2 py-1 rounded text-sm mr-3">
                          Week {week.week}
                        </span>
                        {week.theme}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {week.goals && week.goals.length > 0 && (
                          <div>
                            <h4 className="text-gray-300 font-medium mb-2">Goals for this week:</h4>
                            <ul className="space-y-1">
                              {week.goals.map((goal, goalIndex) => (
                                <li key={goalIndex} className="text-white flex items-start">
                                  <Target className="w-4 h-4 text-rose-400 mr-2 mt-0.5 flex-shrink-0" />
                                  {goal}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {week.daily_actions && week.daily_actions.length > 0 && (
                          <div>
                            <h4 className="text-gray-300 font-medium mb-2">Daily Actions:</h4>
                            <ul className="space-y-1">
                              {week.daily_actions.map((action, actionIndex) => (
                                <li key={actionIndex} className="text-white flex items-start">
                                  <CheckCircle className="w-4 h-4 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                                  {action}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {week.success_metrics && week.success_metrics.length > 0 && (
                          <div>
                            <h4 className="text-gray-300 font-medium mb-2">Success Metrics:</h4>
                            <ul className="space-y-1">
                              {week.success_metrics.map((metric, metricIndex) => (
                                <li key={metricIndex} className="text-white flex items-start">
                                  <TrendingUp className="w-4 h-4 text-blue-400 mr-2 mt-0.5 flex-shrink-0" />
                                  {metric}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Challenges */}
            {actionPlan.action_plan.challenges && actionPlan.action_plan.challenges.length > 0 && (
              <div className="space-y-4">
                <h2 className="text-xl font-bold text-white flex items-center">
                  <Zap className="w-5 h-5 mr-2 text-rose-400" />
                  Recommended Challenges
                </h2>
                
                <div className="grid md:grid-cols-2 gap-4">
                  {actionPlan.action_plan.challenges.map((challenge, index) => (
                    <Card key={index} className="bg-white/5 backdrop-blur-md border-white/10 hover:border-white/20 transition-all">
                      <CardHeader>
                        <CardTitle className="text-white text-lg flex items-center">
                          <Award className="w-5 h-5 mr-2 text-yellow-400" />
                          {challenge.challenge_name}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-gray-300 mb-3">{challenge.description}</p>
                        
                        <div className="flex justify-between items-center mb-3">
                          <Badge 
                            className={`
                              ${challenge.difficulty === 'beginner' ? 'bg-green-500/20 text-green-400 border-green-500/30' : ''}
                              ${challenge.difficulty === 'intermediate' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' : ''}
                              ${challenge.difficulty === 'advanced' ? 'bg-red-500/20 text-red-400 border-red-500/30' : ''}
                            `}
                          >
                            {challenge.difficulty}
                          </Badge>
                          
                          {challenge.estimated_time && (
                            <div className="flex items-center text-sm text-gray-400">
                              <Clock className="w-4 h-4 mr-1" />
                              {challenge.estimated_time}
                            </div>
                          )}
                        </div>
                        
                        <Button 
                          size="sm" 
                          className="w-full bg-rose-600 hover:bg-rose-700"
                          onClick={() => navigate('/coaching/dashboard')}
                        >
                          <Play className="w-4 h-4 mr-1" />
                          Start Challenge
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <Card className="bg-white/5 backdrop-blur-md border-white/10">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    className="flex-1 bg-rose-600 hover:bg-rose-700"
                    onClick={handleBackToDashboard}
                  >
                    <BookOpen className="w-4 h-4 mr-2" />
                    Start Learning
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    className="flex-1 border-white/20 text-white hover:bg-white/10"
                    onClick={fetchActionPlan}
                  >
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Refresh Plan
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <Card className="bg-white/5 backdrop-blur-md border-white/10">
            <CardContent className="p-8 text-center">
              <Target className="w-16 h-16 text-rose-400 mx-auto mb-4" />
              <h2 className="text-xl text-white mb-2">Generating Your Action Plan</h2>
              <p className="text-gray-300 mb-4">We're creating a personalized action plan based on your progress and goals.</p>
              <Button onClick={fetchActionPlan} className="bg-rose-600 hover:bg-rose-700">
                <TrendingUp className="w-4 h-4 mr-2" />
                Generate Plan
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ActionPlan;